import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MailEntity } from '@adornis/mail-client/mail-entity.js';
import { EnrichedMailEntity } from '../db/EnrichedMailEntity.js';

/**
 * A single mail, typed as EnrichedMailEntity so the app-local fields (archivedAt) are
 * readable alongside the engine's own ones.
 *
 * The engine's `getByID(EnrichedMailEntity, …)` cannot do this: its resolver selects
 * `_class: { $in: validSubclasses('EnrichedMailEntity') }`, but the documents in the
 * `mail` collection are stored with `_class: 'MailEntity'` — so it matched zero documents
 * and every read came back `null` (silently, since the detail header just renders empty).
 * Like `subscribeEnrichedMailEntitiesPaginatedFiltered`, we therefore read the raw
 * document and rewrite `_class` in memory only, never on disk.
 */
export const getEnrichedMailByID = registerQuery({
  type: () => EnrichedMailEntity,
  operationName: 'getEnrichedMailByID',
  resolve: (mailID: string) => {
    return async (gqlFields: BaseQLSelectionSet<EnrichedMailEntity>) => {
      const mailCollection = await getRawCollection<EnrichedMailEntity>(MailEntity._collectionName);
      const mail = await mailCollection.findOne<EnrichedMailEntity>({ _id: mailID });
      if (!mail) return null;
      (mail as any)._class = EnrichedMailEntity._class;
      return mail;
    };
  },
  params: [{ name: 'mailID', type: () => String }],
});
